import React, { useEffect, useState } from 'react';
import { 
  getAllVersions, 
  uploadAppVersion, 
  approveVersion, 
  rejectVersion, 
  activateVersion, 
  deleteVersion,
  getVersionStatistics,
  getActiveVersionInfo 
} from '../services/appVersions';

const AppVersions = () => {
  const [versions, setVersions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [activeVersionInfo, setActiveVersionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('versions');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    limit: 50
  });
  const [uploadForm, setUploadForm] = useState({
    version: '',
    buildNumber: '',
    changelog: '',
    apkFile: null
  });

  useEffect(() => {
    if (activeTab === 'versions') {
      fetchVersions();
      fetchActiveVersionInfo();
    } else {
      fetchStatistics();
    }
  }, [activeTab, filters]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.limit) params.limit = filters.limit;
      
      const response = await getAllVersions(params);
      setVersions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      alert('Failed to load versions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await getVersionStatistics();
      setStatistics(response.data || null);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      alert('Failed to load statistics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveVersionInfo = async () => {
    try {
      const response = await getActiveVersionInfo();
      setActiveVersionInfo(response.data || null);
    } catch (error) {
      console.error('Failed to fetch active version info:', error);
      setActiveVersionInfo(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.apk')) {
        alert('Please select an APK file');
        e.target.value = '';
        return;
      }
      setUploadForm({ ...uploadForm, apkFile: file });
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.version || !uploadForm.buildNumber || !uploadForm.apkFile) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('version', uploadForm.version);
      formData.append('buildNumber', uploadForm.buildNumber);
      if (uploadForm.changelog) {
        formData.append('changelog', uploadForm.changelog);
      }
      formData.append('apkFile', uploadForm.apkFile);

      await uploadAppVersion(formData);
      alert('Version uploaded successfully! Pending approval.');
      setShowUploadModal(false);
      setUploadForm({ version: '', buildNumber: '', changelog: '', apkFile: null });
      fetchVersions();
      fetchActiveVersionInfo();
    } catch (error) {
      alert('Failed to upload version: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this version?')) return;
    
    try {
      await approveVersion(id);
      alert('Version approved successfully!');
      fetchVersions();
    } catch (error) {
      alert('Failed to approve version: ' + error.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this version?')) return;
    
    try {
      await rejectVersion(id);
      alert('Version rejected successfully!');
      fetchVersions();
    } catch (error) {
      alert('Failed to reject version: ' + error.message);
    }
  };

  const handleActivate = async (id) => {
    if (!window.confirm('Activate this version? This will make it available to all users and deactivate the current active version.')) return;
    
    try {
      await activateVersion(id);
      alert('Version activated successfully!');
      fetchVersions();
    } catch (error) {
      alert('Failed to activate version: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this version? This will also remove the APK file from disk.')) return;
    
    try {
      await deleteVersion(id);
      alert('Version deleted successfully!');
      fetchVersions();
    } catch (error) {
      alert('Failed to delete version: ' + error.message);
    }
  };

  const handleDownload = (id) => {
    const downloadUrl = `${process.env.REACT_APP_API_URL}/app-versions/download/${id}`;
    window.open(downloadUrl, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">App Version Management</h1>
        {activeTab === 'versions' && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-blue-700 text-sm md:text-base w-full sm:w-auto"
          >
            Upload New Version
          </button>
        )}
      </div>

      <div className="mb-6 flex gap-2 md:gap-4 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('versions')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'versions' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Versions
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'statistics' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Statistics
        </button>
      </div>

      {activeTab === 'versions' && (
        <>
          {activeVersionInfo && (
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-xl font-bold text-blue-900">Current Active Version</h2>
                    <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">LIVE</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div>
                      <p className="text-sm text-gray-600">Version</p>
                      <p className="text-lg font-semibold text-gray-900">{activeVersionInfo.version}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Build Number</p>
                      <p className="text-lg font-semibold text-gray-900">{activeVersionInfo.buildNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">File Size</p>
                      <p className="text-lg font-semibold text-gray-900">{formatFileSize(parseInt(activeVersionInfo.fileSize))}</p>
                    </div>
                    {activeVersionInfo.changelog && (
                      <div className="sm:col-span-2">
                        <p className="text-sm text-gray-600">Changelog</p>
                        <p className="text-sm text-gray-900">{activeVersionInfo.changelog}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      const downloadUrl = `${process.env.REACT_APP_API_URL}${activeVersionInfo.apkUrl}`;
                      window.open(downloadUrl, '_blank');
                    }}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2 whitespace-nowrap"
                  >
                    <span>📥</span>
                    Download APK
                  </button>
                  <a
                    href={`${process.env.REACT_APP_API_URL}${activeVersionInfo.apkUrl}`}
                    className="text-blue-600 hover:text-blue-800 text-xs text-center underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Direct Link
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4 flex flex-col sm:flex-row gap-2 md:gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border rounded px-3 py-2 text-sm md:text-base w-full sm:w-auto"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
              className="border rounded px-3 py-2 text-sm md:text-base w-full sm:w-auto"
            >
              <option value="20">20 records</option>
              <option value="50">50 records</option>
              <option value="100">100 records</option>
            </select>

            <button
              onClick={fetchVersions}
              className="bg-blue-600 text-white px-3 md:px-4 py-2 rounded hover:bg-blue-700 text-sm md:text-base w-full sm:w-auto"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading versions...</div>
          ) : (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Version</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Build</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Active</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Size</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Uploaded By</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          No versions found
                        </td>
                      </tr>
                    ) : (
                      versions.map((version) => (
                        <tr key={version.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{version.version}</td>
                          <td className="px-4 py-3 text-sm">{version.build_number}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(version.status)}`}>
                              {version.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {version.is_active ? (
                              <span className="text-green-600 font-semibold">✓ Active</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">{formatFileSize(version.file_size)}</td>
                          <td className="px-4 py-3 text-sm">{version.uploaded_by_email || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(version.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2 flex-wrap">
                              {version.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApprove(version.id)}
                                    className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleReject(version.id)}
                                    className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {version.status === 'approved' && !version.is_active && (
                                <button
                                  onClick={() => handleActivate(version.id)}
                                  className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                                >
                                  Activate
                                </button>
                              )}
                              <button
                                onClick={() => handleDownload(version.id)}
                                className="bg-gray-600 text-white px-2 py-1 rounded text-xs hover:bg-gray-700"
                              >
                                Download
                              </button>
                              {!version.is_active && (
                                <button
                                  onClick={() => handleDelete(version.id)}
                                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'statistics' && (
        <div>
          {loading ? (
            <div className="text-center py-12">Loading statistics...</div>
          ) : statistics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Versions</p>
                    <p className="text-3xl font-bold mt-2">{statistics.total || 0}</p>
                  </div>
                  <div className="text-4xl">📱</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Pending Approval</p>
                    <p className="text-3xl font-bold mt-2 text-yellow-600">{statistics.pending || 0}</p>
                  </div>
                  <div className="text-4xl">⏳</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Approved</p>
                    <p className="text-3xl font-bold mt-2 text-green-600">{statistics.approved || 0}</p>
                  </div>
                  <div className="text-4xl">✅</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Rejected</p>
                    <p className="text-3xl font-bold mt-2 text-red-600">{statistics.rejected || 0}</p>
                  </div>
                  <div className="text-4xl">❌</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Active Version</p>
                    <p className="text-3xl font-bold mt-2 text-blue-600">{statistics.active_version || 'None'}</p>
                  </div>
                  <div className="text-4xl">🚀</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No statistics available
            </div>
          )}
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Upload New Version</h2>
            <form onSubmit={handleUploadSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Version *</label>
                <input
                  type="text"
                  placeholder="e.g., 1.0.4"
                  value={uploadForm.version}
                  onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  disabled={uploading}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Build Number *</label>
                <input
                  type="number"
                  placeholder="e.g., 104"
                  value={uploadForm.buildNumber}
                  onChange={(e) => setUploadForm({ ...uploadForm, buildNumber: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  disabled={uploading}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Changelog (Optional)</label>
                <textarea
                  placeholder="Release notes and changes..."
                  value={uploadForm.changelog}
                  onChange={(e) => setUploadForm({ ...uploadForm, changelog: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  disabled={uploading}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">APK File *</label>
                <input
                  type="file"
                  accept=".apk"
                  onChange={handleFileChange}
                  className="w-full border rounded px-3 py-2"
                  disabled={uploading}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Max size: 200MB</p>
              </div>

              {uploading && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="text-sm font-semibold text-blue-900">Uploading APK file...</p>
                      <p className="text-xs text-blue-700">This may take a few moments for large files</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-4 py-2 rounded font-semibold flex items-center justify-center gap-2 ${
                    uploading 
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadForm({ version: '', buildNumber: '', changelog: '', apkFile: null });
                  }}
                  disabled={uploading}
                  className={`flex-1 px-4 py-2 rounded ${
                    uploading
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppVersions;
